import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [businessId, setBusinessId] = useState(null);

  // Business fields
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [notificationPhone, setNotificationPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [services, setServices] = useState('');
  const [hours, setHours] = useState('');
  const [twilioNumber, setTwilioNumber] = useState('');

  // Option 2 fields
  const [optionType, setOptionType] = useState('basic');
  const [privateNumber, setPrivateNumber] = useState('');

  useEffect(() => {
    const fetchBusiness = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error: fetchError } = await supabase
        .from('businesses')
        .select('id, business_name, owner_name, business_phone, notification_phone, website_url, services, hours, twilio_number, option_type, private_number')
        .eq('owner_email', user.email)
        .single();

      if (fetchError) { setError('Unable to load settings.'); setLoading(false); return; }

      if (data) {
        setBusinessId(data.id);
        setBusinessName(data.business_name || '');
        setOwnerName(data.owner_name || '');
        setBusinessPhone(data.business_phone || '');
        setNotificationPhone(data.notification_phone || '');
        setWebsiteUrl(data.website_url || '');
        setServices(data.services || '');
        setHours(data.hours || '');
        setTwilioNumber(data.twilio_number || '');
        setOptionType(data.option_type || 'basic');
        setPrivateNumber(data.private_number || '');
      }
      setLoading(false);
    };
    fetchBusiness();
  }, []);

  const handleSave = async () => {
    if (!businessId) return;
    setSaving(true);
    setError('');
    setSaved(false);

    const payload = {
      business_name: businessName.trim(),
      owner_name: ownerName.trim(),
      business_phone: businessPhone.trim(),
      notification_phone: notificationPhone.trim(),
      website_url: websiteUrl.trim() || null,
      services: services.trim() || null,
      hours: hours.trim() || null,
      option_type: optionType,
      private_number: optionType === 'complete' ? (privateNumber.trim() || null) : null,
    };

    const { error: updateError } = await supabase.from('businesses').update(payload).eq('id', businessId);

    if (updateError) { setError('Unable to save settings. Please try again.'); setSaving(false); return; }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    border: '1px solid #DDE3F0', fontSize: '14px', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0C1A2E',
    boxSizing: 'border-box', backgroundColor: '#fff',
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#0C1A2E', marginBottom: '6px' };
  const sectionStyle = { backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', padding: '24px', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', marginBottom: '20px' };
  const sectionTitle = { margin: '0 0 20px', fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: '#0C1A2E' };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', color: '#9AABBE', fontSize: '14px' }}>Loading settings…</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>

        <header style={{ marginBottom: '28px' }}>
          <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>Settings</h1>
          <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px' }}>Update your business info and ReplyFirst plan.</p>
        </header>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        )}

        {/* ReplyFirst Number */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Your ReplyFirst Number</h2>
          <div style={{ padding: '14px 16px', backgroundColor: '#EEF3FF', borderRadius: '10px', border: '1px solid #C3D2F7' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>ReplyFirst Number</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: '#0C1A2E', fontFamily: "'Fraunces', serif" }}>
              {twilioNumber || 'Not assigned yet'}
            </div>
            {twilioNumber && <div style={{ fontSize: '12px', color: '#9AABBE', marginTop: '4px' }}>This is the number your customers' calls forward to.</div>}
          </div>
        </div>

        {/* Plan Selection */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>ReplyFirst Plan</h2>

          {/* Option 1 */}
          <div
            onClick={() => setOptionType('basic')}
            style={{ padding: '16px', borderRadius: '10px', border: `2px solid ${optionType === 'basic' ? '#1A56DB' : '#DDE3F0'}`, backgroundColor: optionType === 'basic' ? '#EEF3FF' : '#fff', cursor: 'pointer', marginBottom: '12px', transition: 'all 0.15s' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${optionType === 'basic' ? '#1A56DB' : '#DDE3F0'}`, backgroundColor: optionType === 'basic' ? '#1A56DB' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {optionType === 'basic' && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#0C1A2E', fontSize: '15px' }}>Basic — $97/month</div>
                <div style={{ fontSize: '12px', color: '#5A6B84' }}>Best for cell phones and landlines</div>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#3D4F66', lineHeight: 1.7, paddingLeft: '28px' }}>
              ✓ Unanswered calls forward to ReplyFirst after 15 seconds<br />
              ✓ Joanna answers, customer gets texted instantly<br />
              ✓ Voicemail recorded and saved in your dashboard<br />
              ✓ Works on any phone — cell, landline, or VoIP
            </div>
          </div>

          {/* Option 2 */}
          <div
            onClick={() => setOptionType('complete')}
            style={{ padding: '16px', borderRadius: '10px', border: `2px solid ${optionType === 'complete' ? '#1A56DB' : '#DDE3F0'}`, backgroundColor: optionType === 'complete' ? '#EEF3FF' : '#fff', cursor: 'pointer', transition: 'all 0.15s' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${optionType === 'complete' ? '#1A56DB' : '#DDE3F0'}`, backgroundColor: optionType === 'complete' ? '#1A56DB' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {optionType === 'complete' && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: '#0C1A2E', fontSize: '15px' }}>Complete — $147/month</div>
                <div style={{ fontSize: '12px', color: '#5A6B84' }}>Best for VoIP systems with a receptionist</div>
              </div>
            </div>
            <div style={{ fontSize: '13px', color: '#3D4F66', lineHeight: 1.7, paddingLeft: '28px' }}>
              ✓ Every call comes through ReplyFirst first<br />
              ✓ Live calls connect normally — no interruption<br />
              ✓ Hangups and no-answers get texted immediately<br />
              ✓ Your existing VoIP voicemail stays completely untouched<br />
              ✓ Customers calling back your ReplyFirst number connect to your shop
            </div>
          </div>

          {/* Option 2 setup checklist + private number */}
          {optionType === 'complete' && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ padding: '16px', backgroundColor: '#FFF7ED', borderRadius: '10px', border: '1px solid #FED7AA', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#92400E', marginBottom: '10px' }}>
                  📋 Complete Plan Setup Checklist
                </div>
                {[
                  'Get a second phone number (DID) from your VoIP provider that rings the same phones as your main number',
                  'Ask your VoIP provider to set voicemail on that number to answer after 25 seconds',
                  'In your VoIP admin portal, set your main public number to forward ALL calls immediately to your ReplyFirst number above',
                  'Enter your private back-door number below and click Save',
                  'Call or text Cole at (614) 795-4971 to run the 4 setup tests together',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', fontSize: '13px', color: '#92400E' }}>
                    <span style={{ fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div>
                <label style={labelStyle}>Private back-door number <span style={{ color: '#F97316' }}>*</span></label>
                <input
                  type="tel"
                  value={privateNumber}
                  onChange={e => setPrivateNumber(e.target.value)}
                  placeholder="e.g. (704) 555-9999"
                  style={inputStyle}
                />
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#9AABBE' }}>
                  The second number from your VoIP that rings your shop phones. Contact Cole at (614) 795-4971 for help getting this set up.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Business Info */}
        <div style={sectionStyle}>
          <h2 style={sectionTitle}>Business Information</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Business name</label>
                <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Your name</label>
                <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Business phone</label>
                <input type="tel" value={businessPhone} onChange={e => setBusinessPhone(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Lead notification phone</label>
                <input type="tel" value={notificationPhone} onChange={e => setNotificationPhone(e.target.value)} style={inputStyle} />
                <p style={{ marginTop: '4px', fontSize: '12px', color: '#9AABBE' }}>Where lead summaries are texted</p>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Website URL</label>
              <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://www.yourbusiness.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Services offered</label>
              <input type="text" value={services} onChange={e => setServices(e.target.value)} placeholder="e.g. Oil changes, brakes, tires, engine repair" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Business hours</label>
              <input type="text" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. Mon-Fri 8am-6pm, Sat 9am-3pm" style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
            backgroundColor: saved ? '#22C55E' : saving ? '#93AEED' : '#F97316',
            color: '#fff', fontSize: '15px', fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.15s',
          }}
        >
          {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save settings'}
        </button>

      </div>
    </div>
  );
}

export default Settings;